import { Box, Icon, PaginationButton, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import useOffsetPages from 'modules/core/components/DataTable/components/Pagination/useOffsetPages'
import i18n from 'modules/core/lib/i18n'

// This file is copied from `modules/core/components/DataTable/components/Pagination/OffsetPagination.tsx`
// and modified to accept props rather than rely on `usePagination`

const MAX_OFFSET_PAGES = 7

interface ItemsIndicatorProps {
  itemsPerPage: number
  currentPage: number
  total: number
}

const ItemsIndicator = ({ itemsPerPage, currentPage, total }: ItemsIndicatorProps) => {
  if (!total) return null

  const start = (currentPage - 1) * itemsPerPage + 1
  const end = Math.min(start + itemsPerPage - 1, total)

  return (
    <Text.Normal size="200" color="grey1000">
      {i18n.t('data_table.pagination.items_indicator', { start, end, total })}
    </Text.Normal>
  )
}

interface PaginationProps {
  itemsPerPage: number
  currentPage: number
  totalPages: number
  totalCount: number
  goToPage: (newPage: number) => void
}

const Pagination = ({
  itemsPerPage,
  currentPage,
  goToPage,
  totalPages,
  totalCount,
}: PaginationProps) => {
  const pages = useOffsetPages(totalPages ?? 0, currentPage, MAX_OFFSET_PAGES, goToPage)

  if (!totalPages) return null

  const prevButtonDisabled = currentPage === 1
  const nextButtonDisabled = currentPage === totalPages

  return (
    <Box
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      gap="s24"
      width="full"
    >
      <ItemsIndicator currentPage={currentPage} itemsPerPage={itemsPerPage} total={totalCount} />
      <Box
        flexDirection="row"
        gap="s6"
        justifyContent="center"
        alignItems="center"
        component="nav"
        aria-label={i18n.t('common.pagination.wrapper')}
      >
        <PaginationButton
          content={
            <Icon.Medium icon="chevron-left" color={prevButtonDisabled ? 'grey400' : 'grey600'} />
          }
          ariaLabel={i18n.t('common.pagination.previous')}
          onClick={() => goToPage(currentPage - 1)}
          disabled={prevButtonDisabled}
          nonInteractive
        />
        <Box flexDirection="row" gap="s4" alignItems="center">
          {pages.map(({ key, content, onClick, highlighted }) => (
            <Box aria-current={highlighted ? 'true' : 'false'} key={key} component="span">
              <PaginationButton
                content={content}
                ariaLabel={i18n.t('common.pagination.go_to_page', { page: content })}
                onClick={onClick}
                highlighted={highlighted}
              />
            </Box>
          ))}
        </Box>
        <PaginationButton
          content={
            <Icon.Medium icon="chevron-right" color={nextButtonDisabled ? 'grey400' : 'grey600'} />
          }
          ariaLabel={i18n.t('common.pagination.next')}
          onClick={() => goToPage(currentPage + 1)}
          disabled={nextButtonDisabled}
          nonInteractive
        />
      </Box>
    </Box>
  )
}

export default observer(Pagination)
